// What the widget does when it loads (WidgetShell, once per page load):
//   * back from Paystack with a payment reference: "Confirming your
//     payment…" at once when this tab kept the trip, the confirmation reader
//     (the webhook usually lands first), then the return-page check
//     (paystack-payment-status, #1466), which asks Paystack at once while the
//     reader keeps watching. What it answers decides the view
//     (state/payment-return.ts: returnStep):
//       paid / confirmed  "Payment received — we're confirming your
//                         booking…" and the reader is asked until the
//                         webhook's confirmation is on file; when that wait
//                         ends, Paystack is asked once more (a refund
//                         since?); never Pay;
//       in_progress       "Your payment is still going through." with Check
//                         again, asked again by itself a few times while the
//                         reader keeps watching; never Pay;
//       not_paid          (Paystack said so, fresh: a cached "not paid" is
//                         asked again first) the checkout this tab kept
//                         reopens on Review & pay, "We haven't received your
//                         payment. Your details are saved."; the
//                         second-charge guard still checks before any new
//                         Paystack page (its refusals are the checkout's own
//                         notices, useCheckoutSubmit);
//       refunded          "This payment was refunded.", nothing to pay;
//       unknown           the rest of the wait as before (the reader for
//                         about 20 seconds from the return), then "We're
//                         still confirming…" with Check again and, when this
//                         tab kept the checkout, Back to your checkout behind
//                         the check-first words.
//     The reader itself says Confirmed ONLY for a confirmed booking
//     (20261001160010). Its other answers decide the view at once, whatever
//     Paystack says: payment_received_not_confirmed is "Payment received"
//     (the webhook is still deciding; never Pay), and payment_refunded ends
//     the return: no booking, "We couldn't keep this booking, so your payment
//     is being refunded in full." with Choose another time; the booking
//     standing (another payment paid for it), that it is confirmed and this
//     payment goes back. Never the confirmation, never the ad conversion.
//     payment_superseded (paid on a page opened before the booking changed,
//     20261003200010) ends the return too: the payment goes back in full,
//     and while the booking is held "Pay R1,500" opens the venue's pay page
//     for it (superseded_payment_pay_link: the cart priced as it stands now,
//     never this tab's kept lines, which are the old ones); with the booking
//     paid by another payment, nothing to do; confirmed but not paid, the
//     venue collects it; with no booking held, Choose another time.
//     Paid and still going are kept on the trip record (its `said`), so a
//     reload whose check fails says the same again (never the wait's way
//     back to Pay). The check needs proof that this tab owns the payment:
//     the cart token kept with the trip (the Paystack hop record, or the
//     resume entry whose own trip it was). With none (another tab's payment,
//     a gift card) it is not asked and the wait is as before. Never a silent
//     drawer (pay-now spec §7.6).
//   * no reference, a checkout kept in this tab with a trip to Paystack (a
//     reload during the return, or the browser's own Back from Paystack):
//     the entry is marked returned and it is the same return again, never
//     Pay by itself. The same for a trip Paystack sent back whose checkout
//     the entry could not keep (a spa treatment, add-ons: the hop record).
//     A trip Paystack already said was not paid, whose checkout reopened
//     (the entry's notPaid: the guest moved on to another page with it
//     open) is asked about quietly: nothing opens first; a hold that has
//     ended, or a cart that changed, is forgotten without a word; no answer
//     to trust ends without a word too (asked again next load); only the
//     money views (paid, still going, refunded, confirmed) or the checkout
//     itself open the drawer.
//   * no reference, a checkout kept in this tab (sessionStorage, §7.5) with
//     no trip to Paystack: checked against the server and restored where the
//     guest was.
// The reopened checkout fills back in what the guest gave: Special requests
// and a stay's Country from the unpaid payment (the check's `details`, the
// server's copy), the marketing tick from the entry; the notice lists what
// must be done again (the terms tick, a code). A cart that changed is said
// so, never "its hold has ended".
// Closing the drawer ends every ask in flight: nothing reopens by itself.
import { useEffect, useRef, useState } from "react";
import { useCart } from "../../state/cart";
import {
  fetchPaystackReturnConfirmation,
  readPaystackReturnReference,
  stripPaystackReturnReference,
  type PaystackReturnConfirmation,
} from "../../state/paystack-return";
import {
  PAYMENT_REFUNDED,
  PAYMENT_SUPERSEDED,
  RECEIVED_LATE_ASK_AT_MS,
  RECEIVED_NOT_CONFIRMED,
  SUPERSEDED_PAY_FAILED,
  refundInFull,
} from "../../state/confirmation-outcome";
import {
  BACK_TO_CHECKOUT_CHANGED_MESSAGE,
  BACK_TO_CHECKOUT_LAPSED_MESSAGE,
  CONFIRM_POLL_AT_MS,
  pollPaystackConfirmation,
  reopenedFields,
  resumeNotice,
  withoutCodes,
  type CheckoutDetails,
  type CheckoutResume,
  type ReopenedFields,
} from "../../state/checkout-steps";
import {
  PAID_POLL_AT_MS,
  STATUS_RECHECK_MS,
  hopSaid,
  initialPhase,
  notPaidNotice,
  phaseForAnswer,
  proofFor,
  recheckKind,
  remainingSchedule,
  returnStep,
  saidAfter,
  type PaystackSaid,
  type RecheckKind,
  type RefundKind,
  type RecheckTiming,
  type ReturnPhase,
} from "../../state/payment-return";
import { readPaymentStatus, rememberPaystackHop } from "../../state/charge-guard";
import type { ConfirmationSnapshot, PaymentReturnState } from "../CartPanel";
import { payLinkMeansNotHeld, supersededPayLink } from "../../state/superseded-pay";
import {
  clearCheckoutResume,
  clearPaystackHop,
  keptHoldsEnded,
  markCheckoutNotPaid,
  markCheckoutReturned,
  markPaystackHopReturned,
  readCheckoutResume,
  readPaystackHop,
  rememberPaystackSaid,
  restoreCheckout,
  type RestoreProblem,
  type ResumedCheckout,
} from "./useCheckoutResume";

type Confirmed = Extract<PaystackReturnConfirmation, { ok: true }>;

/** Reopening the kept checkout from "We're still confirming…" did not work, and the entry is still there. */
const BACK_TO_CHECKOUT_RETRY_MESSAGE = "We couldn't reopen your checkout just now. Please try again.";

/** Why the kept checkout did not reopen, in the view's words. */
const RESTORE_PROBLEM_MESSAGE: Record<RestoreProblem, string> = {
  lapsed: BACK_TO_CHECKOUT_LAPSED_MESSAGE,
  changed: BACK_TO_CHECKOUT_CHANGED_MESSAGE,
  unanswered: BACK_TO_CHECKOUT_RETRY_MESSAGE,
};

export function useCheckoutReturn({
  tenantSlug,
  openCart,
  onConfirmed,
  schedule = CONFIRM_POLL_AT_MS,
  paidSchedule = PAID_POLL_AT_MS,
  lateSchedule = RECEIVED_LATE_ASK_AT_MS,
  recheck = STATUS_RECHECK_MS,
}: {
  tenantSlug: string;
  /** Open the drawer (the shell's cartOpen). */
  openCart: () => void;
  /** A confirmed paid booking: the shell's own follow-ups (the ad conversion, the success tab). */
  onConfirmed?: (result: Confirmed) => void;
  /** When to ask the reader again with no answer to trust (ms after the return began). */
  schedule?: readonly number[];
  /** When to ask the reader after "Payment received" (and while it is still going through). */
  paidSchedule?: readonly number[];
  /** When to ask it still, after the late words (ms after them): a refund or the confirmation shows by itself. */
  lateSchedule?: readonly number[];
  /** When the check is asked again by itself. */
  recheck?: RecheckTiming;
}) {
  // A confirmed Paystack return: the drawer opens on the confirmation.
  const [returnConfirmation, setReturnConfirmation] = useState<ConfirmationSnapshot | null>(null);
  // Back from Paystack while the payment is not confirmed yet.
  const [paymentReturn, setPaymentReturn] = useState<PaymentReturnState | null>(null);
  // A full-page checkout restored after a reload or Paystack without paying.
  const [resumedCheckout, setResumedCheckout] = useState<ResumedCheckout | null>(null);
  const started = useRef(false);
  // Ends every ask in flight (the drawer closed): set by the effect below.
  const stopAsking = useRef<() => void>(() => {});
  const { items, add } = useCart();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const latest = useRef({ openCart, onConfirmed });
  latest.current = { openCart, onConfirmed };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const urlReference = readPaystackReturnReference();
    // Paystack's return: what this tab kept remembers it before anything
    // else, so a reload during or after the wait asks about this payment
    // again (each only for its own trip).
    if (urlReference) {
      markCheckoutReturned(tenantSlug, urlReference);
      markPaystackHopReturned(tenantSlug, urlReference);
    }
    let cancelled = false;
    // Each ask (the return, Check again) supersedes the one before: a loop
    // of an older one stops at its next step and says nothing more.
    let generation = 0;
    const live = (g: number) => !cancelled && g === generation;
    // The phase on screen.
    let shownPhase: ReturnPhase | null = null;
    // What Paystack said about the payment on screen, this page load (the
    // trip record keeps it across a reload when there is one).
    let said: PaystackSaid | null = null;
    // A trip Paystack already said was not paid (the entry's notPaid): asked
    // about without opening anything first, until a view is shown.
    let quiet = false;
    // The reader's watch while Paystack is asked and while the payment is
    // still going through.
    let stopWatch: (() => void) | null = null;
    const endWatch = () => {
      stopWatch?.();
      stopWatch = null;
    };
    stopAsking.current = () => {
      generation += 1;
      endWatch();
      shownPhase = null;
    };
    const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    // `fields`: what the reopened checkout fills in (notPaid's: the unpaid
    // payment's copy, and a new page after a handed-back one); by default
    // the entry's tick alone, and never a new page (Paystack said nothing).
    const restore = (
      entry: CheckoutResume,
      notice: string | null,
      fields: ReopenedFields | null = entry.payment ? reopenedFields(entry, null) : null,
      noticeOnceCodesBack: string | null = notice,
    ) =>
      restoreCheckout({
        tenantSlug,
        entry,
        notice,
        noticeOnceCodesBack,
        fields,
        cartIsEmpty: () => itemsRef.current.length === 0,
        add,
      });

    const showRestored = (resumed: ResumedCheckout) => {
      shownPhase = null;
      // The kept checkout now carries its own trip (proofFor reads it): the
      // trip record is done with, so a checkout forgotten later (closed, its
      // hold ended) leaves nothing that opens by itself.
      const hop = readPaystackHop(tenantSlug);
      if (hop && resumed.payment && hop.reference === resumed.payment.reference) clearPaystackHop(tenantSlug);
      setPaymentReturn(null);
      setResumedCheckout(resumed);
      latest.current.openCart();
    };

    const showConfirmed = (result: Confirmed) => {
      endWatch();
      generation += 1;
      // Paid and confirmed: what this tab kept is done with.
      clearCheckoutResume(tenantSlug);
      clearPaystackHop(tenantSlug);
      shownPhase = null;
      setPaymentReturn(null);
      setReturnConfirmation({
        customerFirstName: result.customer.first_name ?? null,
        customerEmail: result.customer.email ?? null,
        customerPhone: result.customer.phone ?? null,
        bookingReference: result.primary_booking_reference ?? null,
        // Lead the success copy with WHAT was booked — previously dropped on
        // this path, so event-ticket buyers saw only the tenant name.
        serviceNames: Array.from(
          new Set(
            (result.bookings ?? [])
              .map((b) => b.service_name)
              .filter((n): n is string => Boolean(n)),
          ),
        ),
        // Event-ticket purchases carry their issued QR tickets + a manage link.
        tickets: result.tickets ?? [],
        manageUrl: result.manage_url ?? null,
        // Paid via Paystack — the value-accurate GA4 + Meta purchase fire
        // server-side (Phase 4), so the client purchase is suppressed here.
        amountCents: null,
      });
      latest.current.onConfirmed?.(result);
      latest.current.openCart();
    };

    const show = (state: PaymentReturnState) => {
      quiet = false;
      shownPhase = state.phase;
      setPaymentReturn(state);
      latest.current.openCart();
    };

    // What Paystack said, for this page load and (on the trip record) the next.
    const remember = (reference: string, next: PaystackSaid | null) => {
      said = next;
      rememberPaystackSaid(tenantSlug, reference, next);
    };

    // The views that carry their own Check again.
    const showGoing = (reference: string) => show({ phase: "going", onCheckAgain: () => checkAgain(reference) });
    // The late words, with Check again, and the reader still asked (every
    // 30 seconds up to 30 minutes from the return), so a refund, or the
    // confirmation, still shows by itself. Check again, or the drawer
    // closing, ends these asks (a new generation).
    const showReceivedLate = (reference: string, g: number) => {
      show({ phase: "received", late: true, onCheckAgain: () => checkAgain(reference) });
      void pollPaystackConfirmation(reference, {
        schedule: lateSchedule,
        fetch: fetchPaystackReturnConfirmation,
        cancelled: () => !live(g),
      }).then((result) => {
        if (live(g)) settled(result, reference);
      });
    };

    // "Still confirming": where the confirmation will arrive, Check again,
    // and — when this tab kept the checkout — the way back to it (with no
    // answer from Paystack the guest who cancelled reads exactly like a late
    // webhook, so the choice is theirs, next to the words that say to check
    // first).
    const showStill = (reference: string, problem: string | null = null) => {
      const canGoBack = readCheckoutResume(tenantSlug) !== null;
      const state: PaymentReturnState = {
        phase: "still",
        onCheckAgain: () => checkAgain(reference),
        onBackToCheckout: canGoBack ? () => void backToCheckout(reference, state) : undefined,
        problem,
      };
      show(state);
    };

    const backToCheckout = async (reference: string, still: PaymentReturnState) => {
      const g = ++generation;
      const entry = readCheckoutResume(tenantSlug);
      if (!entry) {
        showStill(reference, BACK_TO_CHECKOUT_LAPSED_MESSAGE);
        return;
      }
      setPaymentReturn({ ...still, backBusy: true, problem: null });
      const resumed = await restore(entry, resumeNotice(entry), undefined, resumeNotice(withoutCodes(entry)));
      if (!live(g)) return;
      if (resumed.ok) {
        showRestored(resumed.checkout);
        return;
      }
      // Cleared: the hold ended, or the cart changed. Still kept: the server
      // did not answer; the guest can try again.
      showStill(reference, RESTORE_PROBLEM_MESSAGE[resumed.problem]);
    };

    // No answer to trust: the reader for the rest of the ~20 seconds from
    // `since` (the return, or Check again), as before, then where the
    // confirmation will arrive.
    const waitThenStill = async (reference: string, g: number, since: number) => {
      const result = await pollPaystackConfirmation(reference, {
        schedule: remainingSchedule(schedule, Date.now() - since),
        fetch: fetchPaystackReturnConfirmation,
        cancelled: () => !live(g),
      });
      if (!live(g)) return;
      if (settled(result, reference)) return;
      if (result.reason === "not_a_booking") {
        // Not a booking (a gift card): its own flow owns the return.
        setPaymentReturn(null);
        return;
      }
      if (result.reason === RECEIVED_NOT_CONFIRMED) {
        // The reader says the money is in: "Payment received", never the
        // wait's way back to Pay.
        await received(reference, g);
        return;
      }
      if (quiet) {
        // A trip Paystack already said was not paid, and no answer to trust
        // now: nothing to open. The entry stays, so the next load asks again.
        setPaymentReturn(null);
        return;
      }
      showStill(reference);
    };

    // A refund is on file: the money went back. Nothing of this trip is
    // kept (there is nothing to pay or reopen), and nothing leads to Pay.
    // `refund`: what the confirmation reader said (no booking, or the
    // booking stands); null on Paystack's word alone. Final, like the
    // confirmation: every ask still in flight stops.
    const showRefunded = (refund: RefundKind | null = null, inFull = true) => {
      endWatch();
      generation += 1;
      clearCheckoutResume(tenantSlug);
      clearPaystackHop(tenantSlug);
      said = null;
      show({ phase: "refunded", refund, refundInFull: inFull });
    };

    // Paid on a page opened before the booking changed (20261003200010): the
    // payment goes back in full. Final, like a refund: every ask in flight
    // stops, and the trip to the old page and the checkout kept for it are
    // forgotten (its lines are the old ones: never reopened). While the
    // booking is held, Pay the updated total opens the venue's pay page for
    // it, which prices the cart as it stands now.
    const showSuperseded = (answer: PaystackReturnConfirmation, reference: string) => {
      endWatch();
      generation += 1;
      clearPaystackHop(tenantSlug);
      clearCheckoutResume(tenantSlug);
      said = null;
      const held = !answer.ok && answer.booking_held === true && answer.booking_confirmed !== true;
      const payable = held && !answer.ok && answer.can_pay_updated !== false;
      const view: PaymentReturnState = {
        phase: "superseded",
        superseded: answer,
        onPayUpdated: payable ? () => void payUpdated(view, reference) : undefined,
      };
      show(view);
    };

    const payUpdated = async (view: PaymentReturnState, reference: string) => {
      const g = ++generation;
      setPaymentReturn({ ...view, backBusy: true, problem: null });
      const link = await supersededPayLink(reference);
      if (!live(g)) return;
      if (link.ok) {
        window.location.href = link.payUrl;
        return;
      }
      if (payLinkMeansNotHeld(link.reason) && view.superseded && !view.superseded.ok) {
        // Nothing to pay for here any more: the booking is no longer held.
        show({
          ...view,
          superseded: { ...view.superseded, booking_held: false },
          onPayUpdated: undefined,
          backBusy: false,
          problem: null,
        });
        return;
      }
      show({ ...view, backBusy: false, problem: SUPERSEDED_PAY_FAILED });
    };

    // A final answer from the reader: the confirmation, or a refund. True
    // when it was one (the view is shown and nothing more is asked). Typed as
    // a guard so that after `if (settled(r)) return;` r is a not-ok answer.
    const settled = (result: PaystackReturnConfirmation, reference: string): result is Confirmed => {
      if (result.ok) {
        showConfirmed(result);
        return true;
      }
      if (result.reason === PAYMENT_REFUNDED) {
        showRefunded(result.booking_confirmed === true ? "booked" : "none", refundInFull(result));
        return true;
      }
      if (result.reason === PAYMENT_SUPERSEDED) {
        showSuperseded(result, reference);
        return true;
      }
      return false;
    };

    // Paid: the webhook is confirming it. The reader is asked until its
    // confirmation is on file (about 3 minutes; `late`, Check again after
    // that wait: once). Then Paystack is asked once whether the payment has
    // since been refunded (a payment after the hold ended is refunded, not
    // booked): a refund says so; anything else, the late words, with Check
    // again, while the reader is still asked every 30 seconds up to 30
    // minutes (showReceivedLate). Never Pay: the money is in.
    const received = async (reference: string, g: number, { late = false }: { late?: boolean } = {}) => {
      if (!late) show({ phase: "received" });
      const result = await pollPaystackConfirmation(reference, {
        schedule: late ? [0] : paidSchedule,
        fetch: fetchPaystackReturnConfirmation,
        cancelled: () => !live(g),
      });
      if (!live(g)) return;
      if (settled(result, reference)) return;
      const cartToken = proofFor(reference, readPaystackHop(tenantSlug), readCheckoutResume(tenantSlug));
      if (cartToken) {
        const answer = await readPaymentStatus(reference, cartToken);
        if (!live(g)) return;
        if (phaseForAnswer(answer) === "refunded") {
          showRefunded();
          return;
        }
      }
      showReceivedLate(reference, g);
    };

    // Paystack said not paid: the kept checkout reopens on Review & pay,
    // saying so, with what the guest gave filled back in (`details`, the
    // unpaid payment's copy). With none (a cart the entry cannot keep,
    // another cart's entry) or one that cannot reopen, the view says so
    // instead; on a quiet return (the trip was answered before) it says
    // nothing and forgets what cannot come back.
    const notPaid = async (reference: string, cartToken: string, g: number, details: CheckoutDetails | null) => {
      const entry = readCheckoutResume(tenantSlug);
      const kept = entry && entry.cartToken === cartToken ? entry : null;
      if (!kept) {
        clearPaystackHop(tenantSlug);
        if (quiet) setPaymentReturn(null);
        else show({ phase: "not_paid" });
        return;
      }
      const fields = reopenedFields(kept, details, { notPaid: true });
      const resumed = await restore(kept, notPaidNotice(kept, fields), fields, notPaidNotice(withoutCodes(kept), fields));
      if (!live(g)) return;
      if (resumed.ok) {
        // From here the kept checkout remembers the trip, answered: a reload
        // asks again, quietly.
        clearPaystackHop(tenantSlug);
        markCheckoutNotPaid(tenantSlug, reference);
        showRestored({
          ...resumed.checkout,
          payment: resumed.checkout.payment ? { ...resumed.checkout.payment, notPaid: true } : null,
        });
        return;
      }
      const stillKept = resumed.problem === "unanswered" && readCheckoutResume(tenantSlug) !== null;
      if (!stillKept) clearPaystackHop(tenantSlug);
      if (quiet) {
        // Nothing the guest is waiting for: the next load asks again (still
        // kept), or there is nothing left to reopen.
        setPaymentReturn(null);
        return;
      }
      show({
        phase: "not_paid",
        problem: RESTORE_PROBLEM_MESSAGE[stillKept ? "unanswered" : resumed.problem],
        onReviewAndPay: stillKept
          ? () => {
              const next = ++generation;
              setPaymentReturn((s) => (s ? { ...s, backBusy: true, problem: null } : s));
              void notPaid(reference, cartToken, next, details);
            }
          : undefined,
      });
    };

    // The reader's watch while Paystack is asked and while the payment is
    // still going through: the webhook may land meanwhile.
    const startWatch = (reference: string, g: number) => {
      endWatch();
      let watching = true;
      stopWatch = () => {
        watching = false;
      };
      void pollPaystackConfirmation(reference, {
        // The reader was asked just before (the return, Check again).
        schedule: paidSchedule.filter((at) => at > 0),
        fetch: fetchPaystackReturnConfirmation,
        cancelled: () => !watching || !live(g),
      }).then((result) => {
        if (watching && live(g)) settled(result, reference);
      });
    };

    // Ask the check (Paystack) and act on the answer (returnStep): again by
    // itself while another request holds the Paystack ask (checking), while
    // a "not paid" may predate the return (stale) or while it is still going
    // through; then stay with what it said. `since`: when this return (or
    // Check again) began, for the rest of the reader's wait.
    const follow = async (reference: string, g: number, since: number) => {
      const hop = readPaystackHop(tenantSlug);
      const cartToken = proofFor(reference, hop, readCheckoutResume(tenantSlug));
      if (!cartToken) {
        // Nothing proves this tab owns the payment: the wait as before (no
        // check was asked, so none of it was spent).
        await waitThenStill(reference, g, Date.now());
        return;
      }
      // A hold that "ran out" on the reopened checkout asks about this trip
      // first (explainLapse): it may have been paid in that window.
      rememberPaystackHop(reference);
      said = hopSaid(hop, reference) ?? said;
      // The reader keeps watching while Paystack is asked (a slow Paystack
      // never holds back a webhook that has landed).
      startWatch(reference, g);
      let answer = await readPaymentStatus(reference, cartToken);
      let lastKind: RecheckKind | null = null;
      let attempt = 0;
      for (;;) {
        if (!live(g)) return;
        const kind = recheckKind(answer, said);
        if (kind !== lastKind) {
          attempt = 0;
          lastKind = kind;
        }
        const step = returnStep(answer, { said, attempt }, recheck);
        remember(reference, saidAfter(said, step.phase));
        if (step.phase === "received") {
          endWatch();
          await received(reference, g);
          return;
        }
        if (step.phase === "refunded") {
          showRefunded();
          return;
        }
        if (step.phase === "not_paid") {
          endWatch();
          await notPaid(reference, cartToken, g, answer.details ?? null);
          return;
        }
        if (step.phase === "still") {
          // No answer to trust: the rest of the wait as before.
          endWatch();
          await waitThenStill(reference, g, since);
          return;
        }
        // Going (the reader keeps watching; Check again is there), or still
        // asking ("Confirming…").
        if (step.phase === "going" && shownPhase !== "going") showGoing(reference);
        else if (step.phase === "going") setPaymentReturn((s) => (s && s.checking ? { ...s, checking: false } : s));
        if (step.recheckIn === null) return;
        attempt += 1;
        await wait(step.recheckIn);
        if (!live(g)) return;
        answer = await readPaymentStatus(reference, cartToken);
      }
    };

    // The return (and a reload during it): what Paystack said before, or
    // "Confirming your payment…", at once when this tab kept the trip; the
    // reader first, then Paystack.
    const confirmReturn = async (reference: string) => {
      const g = ++generation;
      const since = Date.now();
      const hop = readPaystackHop(tenantSlug);
      const ours = proofFor(reference, hop, readCheckoutResume(tenantSlug)) !== null;
      said = hopSaid(hop, reference);
      const opening = initialPhase(said);
      const open = () => {
        if (opening === "received") show({ phase: "received" });
        else if (opening === "going") showGoing(reference);
        else show({ phase: "confirming" });
      };
      if (ours && !quiet) open();
      const first = await fetchPaystackReturnConfirmation(reference);
      stripPaystackReturnReference();
      if (!live(g)) return;
      if (settled(first, reference)) return;
      if (first.reason === "not_a_booking") {
        // Not a booking (a gift card): its own flow owns the return.
        setPaymentReturn(null);
        return;
      }
      if (first.reason === RECEIVED_NOT_CONFIRMED) {
        // Paid, and the webhook is still deciding: "Payment received" at
        // once (and on a reload), and the reader until it decides. Paystack
        // need not be asked whether it was paid.
        remember(reference, saidAfter(said, "received"));
        await received(reference, g);
        return;
      }
      if (!ours && !quiet) open();
      await follow(reference, g, since);
    };

    // Check again, from any view: the reader, then Paystack. A view that
    // already has an answer keeps its words while it asks ("Checking…" on
    // the button). After "Payment received" Paystack is asked only whether
    // the payment has been refunded since (nothing turns a payment it said
    // went through back into Pay).
    const checkAgain = (reference: string) => {
      endWatch();
      const g = ++generation;
      if (shownPhase === "received") {
        setPaymentReturn((s) => (s ? { ...s, checking: true } : s));
        void received(reference, g, { late: true });
        return;
      }
      if (shownPhase === "going") setPaymentReturn((s) => (s ? { ...s, checking: true } : s));
      else {
        shownPhase = "confirming";
        setPaymentReturn({ phase: "confirming" });
      }
      const since = Date.now();
      void (async () => {
        const first = await fetchPaystackReturnConfirmation(reference);
        if (!live(g)) return;
        if (settled(first, reference)) return;
        if (!first.ok && first.reason === RECEIVED_NOT_CONFIRMED) {
          remember(reference, saidAfter(said, "received"));
          await received(reference, g);
          return;
        }
        await follow(reference, g, since);
      })();
    };

    const entry = readCheckoutResume(tenantSlug);
    const hop = readPaystackHop(tenantSlug);
    if (urlReference) {
      void confirmReturn(urlReference);
    } else if (entry?.payment) {
      // A trip to Paystack this checkout made, and no reference in the
      // address: a reload during the return, or the browser's own Back from
      // Paystack (Paystack never sent the page back). Either way the same
      // return, never Pay by itself: confirmed → the confirmation; paid or
      // still going → those views; not paid → Review & pay, saying so; no
      // answer → still confirming, Back to your checkout behind the
      // check-first words. Marked returned first, so a reload from any of
      // these views asks again.
      markCheckoutReturned(tenantSlug, entry.payment.reference);
      markPaystackHopReturned(tenantSlug, entry.payment.reference);
      const reference = entry.payment.reference;
      if (entry.payment.notPaid) {
        // Paystack said this trip was not paid and its checkout reopened;
        // the guest moved on with it open. Its holds all ended: nothing can
        // reopen and nothing is said (a payment made after the hold ended is
        // refunded by the webhook, and the guest is told by email). Else
        // the same return, quietly.
        quiet = true;
        void keptHoldsEnded(entry).then((ended) => {
          if (cancelled) return;
          if (ended) {
            clearCheckoutResume(tenantSlug);
            clearPaystackHop(tenantSlug);
            return;
          }
          void confirmReturn(reference);
        });
      } else {
        void confirmReturn(reference);
      }
    } else if (!entry && hop?.returned) {
      // The same, for a checkout the entry could not keep (a spa treatment,
      // add-ons): the trip this tab kept, once Paystack sent it back.
      void confirmReturn(hop.reference);
    } else if (entry) {
      void restore(entry, resumeNotice(entry), undefined, resumeNotice(withoutCodes(entry))).then((resumed) => {
        if (!cancelled && resumed.ok) showRestored(resumed.checkout);
      });
    }
    return () => {
      cancelled = true;
      endWatch();
    };
    // Once per page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    returnConfirmation,
    paymentReturn,
    resumedCheckout,
    /** The drawer closed: nothing of the return is shown again, and nothing still asking reopens it. */
    reset: () => {
      stopAsking.current();
      setReturnConfirmation(null);
      setPaymentReturn(null);
      setResumedCheckout(null);
    },
    /** CartPanel's view and props for what the page load found. */
    panelProps: {
      initialView: returnConfirmation
        ? ("confirmed" as const)
        : paymentReturn
          ? ("returning" as const)
          : resumedCheckout
            ? ("checkout" as const)
            : undefined,
      initialConfirmation: returnConfirmation ?? undefined,
      // A new mount for each of these: the drawer opens in its own view.
      key: returnConfirmation ? "confirmed" : paymentReturn ? "returning" : resumedCheckout ? "resumed" : "cart",
      resume: resumedCheckout,
      paymentReturn,
    },
  };
}
